import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { capitalize } from 'lodash-es'

import { DropdownItemType, PopOverItemType } from 'components'

import {
  CampaignLookupItem,
  GenericItem,
  GetContactTypeResponse,
  GetLookupsResponse,
  CallbackRoutingItem,
} from 'services/types/lookup'
import { LeadType } from 'services/types/settings'
import { LeadProcessItem, Workflow } from 'types/store'
import { FIELD_TYPES, LOOKUP_FIELDS, STALE_TIME } from 'utils/constants'
import { computeDurationMinutes } from 'utils/datetime'
import { CustomFieldAccessor, LeadOwnerType, QuotationReportTemplateType } from 'types/global'
import { AppQueryOptions, useAppQueryMap } from 'hooks/use-app-query'
import { getLookup, getDurations } from 'services/lookup'
import { lookupKey } from 'utils/constants/queryKey'
import useBranch from 'hooks/use-branch'
import useUserPermissions from 'hooks/use-user-permissions'

type Props = {
  canAddLeadCampaign?: boolean
  canAddLeadSource?: boolean
  includeUnassigned?: boolean
  onlyValidCampaigns?: boolean
  sendAllCampaignsAsDefault?: boolean
  leadSourceId?: string
  leadCampaignId?: string
  queryOptions?: AppQueryOptions
  isLookUpCachable?: boolean
  includeAllUsers?: boolean
  includeActiveTeams?: boolean
}

export type UserTeamOption = DropdownItemType<string> & { data: LeadOwnerType }
export type UserTeamPopOver = PopOverItemType<string> & { data: LeadOwnerType }

export type TeamOptions = {
  label: string
  value: string
  isDisabled: boolean
  teamMembers: string[]
  isTeamActive: boolean
}

export type TeamOptionDropdown = {
  fname?: string | null
  lname?: string | null
  name: string
  teamMembers: string[]
  id: string
  isActive: boolean
  type: string
}

type Result = {
  callbackRouting: CallbackRoutingItem[]
  callbackRoutingOptions: DropdownItemType<string, CallbackRoutingItem>[]
  leadCampaignOptions: DropdownItemType<string, CampaignLookupItem>[]
  leadProcessColors: string[]
  leadProcesses: Array<{
    id: string
    name: string
    isDefault: boolean
    leadStatuses: Array<{
      id: string
      name: string
    }>
  }>
  leadProcessOptions: DropdownItemType<string>[]
  leadSourceOptions: DropdownItemType<string>[]
  leadStatuses: Workflow['leadStatuses']
  leadTypeOptions: DropdownItemType<string>[]
  industries: DropdownItemType<string>[]
  leadTypes: LeadType[]
  mobileContactInfoTypeOptions: DropdownItemType<string>[]
  teamOptions: DropdownItemType<string>[]
  userOptions: DropdownItemType<string>[]
  users: Workflow['users']
  contactInfoTypes: GetContactTypeResponse[]
  contactTypes: GenericItem[]
  fieldTypes: Array<{
    id: number
    type: CustomFieldAccessor
    name: string
  }>
  fieldTypeOptions: DropdownItemType<number>[]
  leadCampaigns: Array<CampaignLookupItem>
  userTeamOptions: Array<UserTeamOption>
  opportunityStatusOptions: DropdownItemType<string>[]
  opportunityConfidenceOptions: DropdownItemType<string>[]
  quotationStatusOptions: DropdownItemType<string>[]
  potentialOptions: DropdownItemType<string>[]
  calendarCycle: DropdownItemType<string>[]
  calendarLength: DropdownItemType<string>[]
  salesObjectiveOptions: DropdownItemType<string>[]
  leadSourceTypeOptions: DropdownItemType<string>[]
  emailTypeOptions: DropdownItemType<string>[]
  emailActivityTypeOptions: Array<{ id: string; name: string }>
  emailOriginTypes: Array<{ id: string; name: string }>
  quotationReportTemplates: QuotationReportTemplateType[]
}

const useWorkFlowLookup = (props?: Props) => {
  const {
    canAddLeadCampaign = false,
    canAddLeadSource = false,
    includeUnassigned = false,
    onlyValidCampaigns = false,
    sendAllCampaignsAsDefault = false,
    leadSourceId,
    leadCampaignId,
    queryOptions,
    isLookUpCachable = true,
    includeAllUsers = false,
    includeActiveTeams = false,
  } = props || {}
  const { selectedBranch } = useBranch()
  const [leadCampaignPermissions] = useUserPermissions('leadCampaign')
  const [leadSourcePermissions] = useUserPermissions('leadSource')

  const { data: workflow, isSuccess, isLoading, isError, error, refetch, isFetching } = useAppQueryMap(
    [selectedBranch?.branchId, ...lookupKey.lookups()],
    getLookup,
    mapWorkflowLookup,
    {
      ...(isLookUpCachable ? { staleTime: STALE_TIME.lookup } : {}),
      ...(queryOptions || {}),
    },
  )

  const { data: durationsData } = useAppQueryMap(
    lookupKey.durations(),
    getDurations,
    (data) => data.map(({ is_default, ...duration }) => ({ ...duration, isDefault: is_default })),
    {
      staleTime: Infinity,
    },
  )

  const result = useMemo(() => {
    const result: Result = {
      callbackRouting: [],
      callbackRoutingOptions: [],
      leadCampaignOptions: [],
      leadProcessColors: [],
      leadProcesses: [],
      leadProcessOptions: [],
      leadSourceOptions: [],
      leadStatuses: [],
      leadTypeOptions: [],
      industries: [],
      leadTypes: [],
      mobileContactInfoTypeOptions: [],
      teamOptions: [],
      userOptions: [],
      users: [],
      contactInfoTypes: [],
      contactTypes: [],
      fieldTypes: [],
      fieldTypeOptions: [],
      leadCampaigns: [],
      userTeamOptions: [],
      opportunityStatusOptions: [],
      opportunityConfidenceOptions: [],
      quotationStatusOptions: [],
      potentialOptions: [],
      calendarCycle: [],
      calendarLength: [],
      salesObjectiveOptions: [],
      leadSourceTypeOptions: [],
      emailTypeOptions: [],
      emailActivityTypeOptions: [],
      emailOriginTypes: [],
      quotationReportTemplates: [],
    }

    if (!workflow) {
      return result
    }

    const {
      callbackRouting,
      contactInfoTypes,
      contactTypes,
      fieldTypes,
      leadCampaigns,
      leadProcessColors,
      leadProcesses,
      leadSources,
      leadStatuses,
      leadTypes,
      teams,
      users,
      industries,
      opportunityStatuses,
      opportunityConfidenceRate,
      potential,
      calendarCycle,
      calendarLength,
      salesObjectives,
      leadSourceTypes,
      emailTypes,
      emailActivityTypes,
      emailOriginTypes,
      quotationReportTemplates,
      quotationStatuses,
    } = workflow

    // Callback Routing
    result.callbackRouting = callbackRouting || []
    result.callbackRoutingOptions = (callbackRouting || []).map((i) => ({
      label: i.name,
      value: i.id.toString(),
      data: i,
    }))

    result.contactTypes = contactTypes

    // Lead Process Colors
    result.leadProcessColors = leadProcessColors

    // Lead Statuses
    result.leadStatuses = leadStatuses

    // Lead types
    result.leadTypes = leadTypes
    result.leadTypeOptions = leadTypes.map(({ id, name }) => ({ label: name, value: String(id) }))

    // Lead campaigns
    const filteredCampaigns = onlyValidCampaigns ? leadCampaigns.filter((i) => i.id) : leadCampaigns

    if (leadSourceId) {
      result.leadCampaignOptions = filteredCampaigns
        .filter((i) => i.lead_source_id === leadSourceId || i.lead_source_id === '')
        .map((i) => ({ label: i.name, value: i.id, data: i }))
    } else if (sendAllCampaignsAsDefault) {
      result.leadCampaignOptions = filteredCampaigns.map((i) => ({ label: i.name, value: i.id, data: i }))
    }

    if (leadCampaignId) {
      result.leadSourceOptions = filteredCampaigns
        .filter((i) => i.id === leadCampaignId)
        .map((i) => ({ label: i.lead_source, value: i.lead_source_id }))
    } else {
      // Sales sources
      result.leadSourceOptions = leadSources.map((i) => ({
        label: i.name,
        value: i.id,
        data: { isDefault: i.isDefault },
      }))
    }

    if (canAddLeadCampaign && leadCampaignPermissions.canCreate) {
      result.leadCampaignOptions.push({
        icon: 'plus',
        label: 'campaign_add_btn',
        type: 'action',
        value: 'add-new',
      })
    }

    result.leadCampaigns = filteredCampaigns

    // Lead processes
    result.leadProcesses = leadProcesses
    result.leadProcessOptions = (() => {
      const defaultPipline = leadProcesses.find((lp) => lp.isDefault)
      const nonDefault = leadProcesses.filter((lp) => !lp.isDefault)
      const sorted: LeadProcessItem[] = defaultPipline ? [defaultPipline, ...nonDefault] : nonDefault

      return sorted.map((i: LeadProcessItem) => ({ label: i?.name as string, value: i?.id as string }))
    })()

    result.industries = industries.map(({ id, name }) => ({ label: name, value: id }))

    // Contact Info types
    result.contactInfoTypes = contactInfoTypes
    result.mobileContactInfoTypeOptions = contactInfoTypes
      .filter(({ isMobileType }) => isMobileType)
      .map((i) => ({ label: i.name, value: i.id }))

    if (canAddLeadSource && leadSourcePermissions.canCreate) {
      result.leadSourceOptions.push({
        icon: 'plus',
        label: 'source_add_btn',
        type: 'action',
        value: 'add-new',
      })
    }

    result.fieldTypes = fieldTypes
    result.fieldTypeOptions = fieldTypes.map((i) => {
      const config = FIELD_TYPES.find((o) => o.accessor === i.type)

      return {
        icon: config?.icon as DropdownItemType<string>['icon'],
        iconColor: config?.iconColor as DropdownItemType<string>['iconColor'],
        data: i,
        label: i.name,
        value: i.id,
      }
    })

    // Teams
    result.teamOptions = teams
      .map((i) => ({
        label: i.name,
        value: i.id,
        isDisabled: i.isDisabled || !i.hasActiveUsers,
        teamMembers: (i.members || []).map((user) => user.user_id),
        isTeamActive: !i.isDisabled && i.hasActiveUsers,
        data: 'team' as LeadOwnerType,
        icon: ['far', 'users'],
        iconColor: 'gray5' as const,
      }))
      .filter((teams) => (includeActiveTeams ? teams.isTeamActive : true))

    // Users
    result.users = users.filter((user) => (includeAllUsers ? true : !user.isDisabled)) // only used for email
    result.userOptions = users
      .filter((user) => (includeAllUsers ? true : !user.isDisabled))
      .map(({ fname, id, lname, photoUrl, isDisabled, email }) => ({
        label: `${fname} ${lname}`,
        value: id,
        email,
        avatar: photoUrl,
        whoIs: 'user',
        isDisabled: isDisabled,
        avatarPlaceHolder: `${fname?.charAt(0) ?? ''}${lname?.charAt(0) ?? ''}`,
      }))

    result.calendarCycle = calendarCycle.map((i) => ({ label: i.name, value: i.id }))
    result.calendarLength = calendarLength.map((i) => ({ label: i.name, value: i.id }))

    // Teams and Users combined
    result.userTeamOptions = [
      ...result.teamOptions.map((i) => ({
        ...i,
        data: 'team' as LeadOwnerType,
        icon: ['far', 'users'],
        iconColor: 'gray5' as const,
      })),
      ...result.userOptions.map((i) => ({ ...i, data: 'user' as LeadOwnerType })),
    ]

    result.opportunityStatusOptions = opportunityStatuses.map((i) => ({
      label: i.statusName,
      value: i.statusId,
    }))
    result.opportunityConfidenceOptions = opportunityConfidenceRate.map((i) => ({
      label: `${i.rate}%`,
      value: i.rate.toString(),
    }))
    result.quotationStatusOptions =
      quotationStatuses?.map((i) => ({
        label: i.name,
        value: i.id,
      })) ?? []

    result.potentialOptions = potential.map((i) => ({
      label: i.name,
      value: i.id,
    }))

    result.salesObjectiveOptions = salesObjectives.map((i) => ({
      label: i.name,
      value: i.id,
    }))

    result.leadSourceTypeOptions = leadSourceTypes.map((i) => ({
      label: i.name,
      value: i.id,
    }))

    result.emailTypeOptions = emailTypes?.map((i) => ({
      label: i.name,
      value: i.id,
    }))

    result.emailActivityTypeOptions = emailActivityTypes
    result.emailOriginTypes = emailOriginTypes

    if (includeUnassigned) {
      const unassignedOption: UserTeamOption = {
        label: 'unassigned_lbl',
        labelColor: 'red',
        color: 'red',
        value: '',
        data: 'user' as LeadOwnerType,
      }

      result.teamOptions = result.teamOptions.length > 0 ? [unassignedOption, ...result.teamOptions] : []
      result.userOptions = [unassignedOption, ...result.userOptions]
      result.userTeamOptions = [unassignedOption, ...result.userTeamOptions]
    }

    // Quotation
    result.quotationReportTemplates = quotationReportTemplates

    return result
  }, [
    canAddLeadCampaign,
    canAddLeadSource,
    includeUnassigned,
    leadCampaignPermissions.canCreate,
    leadSourceId,
    leadCampaignId,
    leadSourcePermissions.canCreate,
    onlyValidCampaigns,
    sendAllCampaignsAsDefault,
    workflow,
    includeActiveTeams,
    includeAllUsers,
  ])

  const { durations, durationOptions } = useMemo(() => {
    if (!durationsData) {
      return {
        durations: [],
        durationOptions: [],
      }
    }

    return {
      durations: durationsData,
      durationOptions: durationsData.map((i) => ({
        label: `${i.value} ${i.unit}`,
        value: computeDurationMinutes(i.value, i.unit).toString(),
      })),
    }
  }, [durationsData])

  const partialFetchLookup = (_fields: Array<keyof typeof LOOKUP_FIELDS>) => {
    refetch() //TODO: Refetch only the fields that are not yet fetched
  }

  return {
    // Values
    ...result,
    durations,
    durationOptions,
    refetch,
    // Status
    isSuccess,
    isLoading: isLoading || isFetching,
    isError,
    error,
    // Fetch callback
    partialFetchLookup,
  }
}

export const useInvalidateLookup = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries(lookupKey.lookups())
  }
}

const mapWorkflowLookup: (data: GetLookupsResponse) => Workflow = (data) => ({
  ...(data.account_statuses && {
    accountStatuses: data.account_statuses,
  }),
  ...(data.callback_routing && {
    callbackRouting: data.callback_routing,
  }),
  ...(data.contact_info_types && {
    contactInfoTypes:
      data.contact_info_types?.map(({ id, is_default, is_mobile_type, name }) => ({
        id,
        isDefault: is_default,
        isMobileType: is_mobile_type,
        name,
      })) || [],
  }),
  ...(data.contact_types && {
    contactTypes: data.contact_types,
  }),
  ...(data.field_types && {
    fieldTypes: data.field_types,
  }),
  ...(data.industries && {
    industries: data.industries,
  }),
  ...(data.lead_campaigns && {
    leadCampaigns: data.lead_campaigns || [],
  }),
  ...(data.lead_process_colors && {
    leadProcessColors: data.lead_process_colors,
  }),
  ...(data.lead_processes && {
    leadProcesses:
      data.lead_processes?.map(({ id, name, is_default, lead_process_stage }) => ({
        id,
        name,
        isDefault: is_default as boolean,
        leadStatuses: lead_process_stage,
      })) || [],
  }),
  ...(data.lead_source && {
    leadSources: data.lead_source.map(({ is_default, ...i }) => ({ ...i, isDefault: is_default })),
  }),
  ...(data.lead_source_types && {
    leadSourceTypes: data.lead_source_types,
  }),
  ...(data.lead_statuses && {
    leadStatuses:
      data.lead_statuses?.map(({ core_lead_status_id, id, is_end_pipeline, is_negative, name }) => ({
        coreId: core_lead_status_id,
        id,
        isEndPipeline: is_end_pipeline,
        isNegative: is_negative,
        name,
      })) || [],
  }),
  ...(data.lead_types && {
    leadTypes:
      data.lead_types?.map(({ id, is_default, name, color }) => ({
        id,
        isDefault: is_default,
        name,
        color,
      })) || [],
  }),
  ...(data.teams && {
    teams: data.teams.map((item) => ({
      ...item,
      isDisabled: !item.is_active,
      hasActiveUsers: item.has_active_users,
      members: item.members,
    })),
  }),
  ...(data.lead_source && {
    users:
      data.users?.map(({ fname, id, lname, photo_url, is_active, email }) => ({
        fname,
        id,
        lname,
        photoUrl: photo_url,
        isDisabled: typeof is_active === 'boolean' ? !is_active : false,
        email,
      })) || [],
  }),
  ...(data.user_groups && {
    userGroups: data.user_groups,
  }),
  ...(data.opportunity_statuses && {
    opportunityStatuses: data.opportunity_statuses.map((i) => ({
      statusId: i.status_id,
      statusName: i.status_name,
    })),
  }),
  ...(data.opportunity_confidence_rate && {
    opportunityConfidenceRate: data.opportunity_confidence_rate,
  }),
  ...(data.potential && {
    potential: data.potential,
  }),
  calendarCycle: [...(data.calendar_cycle ?? [])],
  calendarLength: [...(data.calendar_length ?? [])],
  ...(data.sales_objectives && {
    salesObjectives: data.sales_objectives.map((i) => ({
      id: i.id,
      name: i.name,
      leadProcessId: i.lead_process_id,
    })),
  }),
  ...(data.email_type && {
    emailTypes: data.email_type,
  }),
  ...(data.email_activity_type && {
    emailActivityTypes: data.email_activity_type,
  }),
  ...(data.email_origin_type && {
    emailOriginTypes: data.email_origin_type,
  }),
  ...(data.quotation_report_templates && {
    quotationReportTemplates: data.quotation_report_templates?.map((template) => ({
      label: capitalize(template.description),
      value: template.id,
      isDefault: template.is_default == '1' ? true : false,
      templateName: template.name,
    })),
  }),
  ...(data.quotation_statuses && {
    quotationStatuses: data.quotation_statuses,
  }),
})

export default useWorkFlowLookup
